import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Box, Slider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  BANNER_PREVIEW_WIDTH,
  BANNER_PREVIEW_HEIGHT,
  BANNER_ASPECT_RATIO,
} from '../MobileBannerPreview/MobileBannerPreview';
import { getCroppedBlob } from './cropImage';
import './BannerImageCropper.css';

// Render the final image at 3x the preview size for crisp output on retina
// screens while keeping the exact banner aspect ratio.
const OUTPUT_SCALE = 3;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

const BannerImageCropper = ({ imageSrc, title = '', placement = '', onCancel, onApply }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_croppedArea, croppedAreaPixelsValue) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const blob = await getCroppedBlob(
        imageSrc,
        croppedAreaPixels,
        BANNER_PREVIEW_WIDTH * OUTPUT_SCALE,
        BANNER_PREVIEW_HEIGHT * OUTPUT_SCALE
      );
      onApply(blob);
    } catch (error) {
      console.error('Error cropping banner image:', error);
      setProcessing(false);
    }
  };

  const clampZoom = value => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

  return (
    <div className="banner-cropper-overlay">
      <div className="banner-cropper-container">
        <div className="banner-cropper-header">
          <Box className="banner-cropper-title">Adjust banner image</Box>
          <button type="button" className="banner-cropper-close" onClick={onCancel}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="banner-cropper-divider" />

        <div className="banner-cropper-body">
          <p className="banner-cropper-hint">
            Drag to reposition and use the slider to zoom. Everything inside the frame is exactly
            what appears on the mobile banner.
          </p>

          <div className="banner-cropper-stage">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={BANNER_ASPECT_RATIO}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              restrictPosition
              showGrid={false}
              // Cover the whole stage so the image is large and the crop band is
              // always filled. The stage is taller than the banner band, so the
              // area outside the band is dimmed — making the selection visible.
              objectFit="cover"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            {/* WYSIWYG overlay positioned exactly over the crop band (full width,
                banner aspect, centered) so the gradient + title/description match
                the mobile banner. pointer-events:none keeps drag/zoom working. */}
            <div
              className="banner-cropper-frame"
              style={{ aspectRatio: `${BANNER_PREVIEW_WIDTH} / ${BANNER_PREVIEW_HEIGHT}` }}
            >
              <div className="banner-cropper-gradient" />
              <div className="banner-cropper-text">
                {title?.trim() ? <div className="banner-cropper-text-title">{title}</div> : null}
                {placement?.trim() ? (
                  <div className="banner-cropper-text-placement">{placement}</div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="banner-cropper-zoom">
            <button
              type="button"
              className="banner-cropper-zoom-btn"
              onClick={() => setZoom(z => clampZoom(z - 0.2))}
              aria-label="Zoom out"
            >
              <RemoveIcon fontSize="small" />
            </button>
            <Slider
              value={zoom}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.01}
              onChange={(_e, value) => setZoom(value)}
              aria-label="Zoom"
              sx={{
                color: '#0a5ef3',
                mx: 1.5,
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 6px rgba(10, 94, 243, 0.16)',
                  },
                },
              }}
            />
            <button
              type="button"
              className="banner-cropper-zoom-btn"
              onClick={() => setZoom(z => clampZoom(z + 0.2))}
              aria-label="Zoom in"
            >
              <AddIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div className="banner-cropper-footer">
          <button
            type="button"
            className="banner-cropper-btn banner-cropper-btn--cancel"
            onClick={onCancel}
            disabled={processing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="banner-cropper-btn banner-cropper-btn--apply"
            onClick={handleApply}
            disabled={processing || !croppedAreaPixels}
          >
            {processing ? 'Applying...' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BannerImageCropper;
